import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import styles from './CommentThread.module.sass'
import Drawer from 'components/Drawer/Drawer'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import useSession from 'hooks/useSession'

import FileListItem from 'components/FileListItem/FileListItem'
import AuthorAndDate from './AuthorAndDate'
import CommentBody from './CommentBody'
import CommentReplies from './CommentReplies'
import { useFetchList } from 'store'
import { ClinicalProcess, Comment } from 'models'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import TextInput from '@jimmyboys/liita-ui/dist/components/TextInput'
import MultiSelect from '@jimmyboys/liita-ui/dist/components/MultiSelect'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { useReload } from 'store'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'
import { useDrawerContext } from 'context/Drawer/DrawerContext'

const CommentThread = ({ handleClose, data }) => {
	const { i18n, openFile, pushNotification, expireSession, showDialog } =
		useGlobal()
	const [openCommentMiniForm, setOpenCommentMiniForm] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const [isPinned, setIsPinned] = useState(false)
	const reload = useReload()
	const location = useLocation()
	const [, , setSearchParams] = useQueryParams(location)
	const { dispatch } = useDrawerContext()
	const repliesEndRef = useRef(null)

	const { processId, id } = data

	const commentUri = `/clinical_processes/${processId}/comments/${id}`
	const commentsRepliesUri = `/clinical_processes/${processId}/comments/${id}/replies`
	const commentsNotificationRecipients = `/clinical_processes/${processId}/comments/notification_recipients`
	const clinicalProcessUri = `/clinical_processes/${processId}`

	const { state, contents } = useFetchList(
		[
			commentUri,
			commentsRepliesUri,
			commentsNotificationRecipients,
			clinicalProcessUri,
		],
		[
			Comment.fromJSON,
			Comment.fromJSON,
			(json) => {
				return {
					value: json.id,
					text: i18n.translate(json.name),
				}
			},
			ClinicalProcess.fromJSON,
		]
	)

	const isLoading = state === 'loading'

	const [comment, repliesData, recipientNotifications = [], clinicalProcess] =
		contents || []

	const causaltyBranch = clinicalProcess?.accident?.segment?.abbreviation

	useEffect(() => {
		setOpenCommentMiniForm(false)
	}, [id])

	const scrollToBottom = () => {
		repliesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
	}

	useEffect(() => {
		scrollToBottom()
	}, [repliesData])

	const clickAddComment = useCallback(() => {
		setOpenCommentMiniForm(true)
	}, [])

	useEffect(() => {
		setIsPinned(comment?.isPinned)
	}, [comment?.isPinned])

	const togglePin = useCallback(() => {
		const url = `${API_URL}/api/clinical_processes/${data?.processId}/comments/pinned`

		setSubmitting(true)
		fetch(
			url,
			fetchOptions({
				method: 'PUT',
				body: { commentId: +id, pinned: isPinned ? false : true },
			})
		)
			.then((r) => parseJSONContent(r))
			.then(() => {
				pushNotification(
					undefined,
					'success',
					isPinned
						? i18n`Comment unpinned successfully!`
						: i18n`Comment pinned successfully!`
				)
				setIsPinned((pin) => !pin)
			})
			.then(() => {
				reload([
					`/clinical_processes/${data?.processId}/comments`,
					commentUri,
				])
				setSubmitting(false)
			})
			.catch((err) => {
				if (err.status === '401' || err.status === 401) {
					expireSession()
				}

				setSubmitting(false)
			})
	}, [
		data?.processId,
		expireSession,
		i18n,
		id,
		isPinned,
		pushNotification,
		reload,
		commentUri,
	])

	const commentOptions = [
		repliesData?.length === 0
			? {
					value: 'add-comment-to-thread',
					text: i18n`Associate with another comment`,
					action: () => {
						showDialog(
							'AddCommentToThread',
							i18n`Associate with another comment`,
							{
								processId: data.processId,
								commentId: data.id,
							},
							false,
							undefined,
							'center',
							'add-comment-to-thread-dialog'
						)
							.then((parent) => {
								reload([
									`/clinical_processes/${data?.processId}/comments`,
									`/clinical_processes/${data?.processId}/comments/${data.id}`,
									`/clinical_processes/${data?.processId}/comments/${data.id}/replies`,
									`/clinical_processes/${data?.processId}/comments/${parent}/replies`,
								])
								setSearchParams({ comment: parent })
								dispatch({
									action: 'OPEN_DRAWER',
									payload: {
										data: {
											processId: data.processId,
											id: parent,
										},
										type: 'CommentThreadDrawer',
									},
								})
							})
							.catch(() => null)
					},
			  }
			: { value: 'no-actions', text: i18n`No action available` },
	]

	return (
		<Drawer
			handleClose={handleClose}
			overrideClasses={{
				dialogHeader: styles['new-dialog-header'],
				closeButton: styles['new-close-button'],
			}}
			customHeader={
				<div className={styles['selected-comment-header']}>
					<HStack align='center' gap='small'>
						<h5>#{data.id}</h5>
						{!isLoading && comment !== null && (
							<div
								className={`${styles['icon-container']} ${
									submitting ? styles['--is-submitting'] : ''
								}`}
								onClick={togglePin}
								disabled={submitting}
							>
								<Icon
									name={`pin${isPinned ? '-filled' : ''}`}
									height='20px'
									width='20px'
									className={`${styles['pin-icon']} ${
										isPinned ? styles['--pinned'] : ''
									}`}
								/>
							</div>
						)}
					</HStack>
					<ModalOptions
						options={commentOptions}
						onSelect={(item) => {
							item && item.action && item.action()
						}}
					>
						<button className={styles['comment-options']}>
							<Icon width='32px' height='32px' name='more' />
						</button>
					</ModalOptions>
				</div>
			}
			dialogId={`comment-thread-${id}`}
		>
			{isLoading ? (
				<div className={styles['horizontal-padding']}>
					<p>{i18n`Loading`}</p>
				</div>
			) : comment === null ? (
				<div className={styles['horizontal-padding']}>
					<p>{i18n`Inexistent comment, or not linked to this clinical process.`}</p>
				</div>
			) : (
				<>
					<div
						className={`${styles['horizontal-padding']} ${styles['no-vertical-padding']}`}
					>
						<h2 className={styles['selected-comment-title']}>
							{comment.title ?? ''}
						</h2>
					</div>
					<div className={styles['selected-comment-main-comment']}>
						<div className={styles['horizontal-padding']}>
							<AuthorAndDate
								author={comment.author}
								date={comment.date.dateTimeFormat}
								linkedTo={comment.linkedTo}
								processId={processId}
								providerEstablishment={
									comment.providerEstablishment
								}
								displayDelay={200}
								notifiedEntities={comment.notifiedEntities}
								notifiedEstablishments={
									comment.notifiedEstablishments
								}
							/>
						</div>
						<div
							className={`${styles['horizontal-padding']} ${styles['content-with-scroll']}`}
						>
							<CommentBody body={comment.body} />
						</div>
					</div>
					{comment.hasFiles && (
						<div
							className={`${styles['comment-file']} ${styles['horizontal-padding']}`}
						>
							{comment.filesOptions.map((file, key) => (
								<FileListItem
									key={key}
									name={file.text}
									onClick={(event) => {
										event.preventDefault()
										openFile && openFile(file)
									}}
								/>
							))}
						</div>
					)}
					<h3
						className={`${styles['horizontal-padding']} ${styles['no-vertical-padding']}`}
					>
						{i18n`Replies`}
					</h3>
					<div className={styles['selected-comment-body']}>
						<VStack>
							<CommentReplies replies={repliesData} />
							<div ref={repliesEndRef} />
						</VStack>
					</div>
					<div className={styles['drawer-footer']}>
						<HStack
							align='center'
							gap='xsmall'
							className={styles['horizontal-padding']}
						>
							<Icon
								name='subdirectory-arrow'
								width='20px'
								height='20px'
								className={styles['footer-arrow']}
							/>
							<button
								className={`${styles['footer-button']} ${
									openCommentMiniForm
										? styles['footer-button-focused']
										: ''
								}`}
								onClick={clickAddComment}
							>
								<small>{i18n`Commentary`}</small>
							</button>
						</HStack>
						{openCommentMiniForm && (
							<CommentMiniForm
								closeMiniForm={() =>
									setOpenCommentMiniForm(false)
								}
								processId={processId}
								commentId={id}
								recipientNotifications={recipientNotifications}
								parentType={comment?.type}
								causaltyBranch={causaltyBranch}
							/>
						)}
					</div>
				</>
			)}
		</Drawer>
	)
}

const CommentMiniForm = ({
	closeMiniForm,
	processId,
	commentId,
	recipientNotifications,
	parentType,
	causaltyBranch,
}) => {
	const { i18n, pushNotification, expireSession } = useGlobal()
	const [comment, setComment] = useState('')
	const [file, setFile] = useState(null)
	const [notify, setNotify] = useState([])
	const [submitting, setSubmitting] = useState(false)
	const { user } = useSession()
	const isDoctor = user?.roles?.includes('doctor')
	const isAT = causaltyBranch === 'AT'
	const hasBackofficeRole = user?.roles?.some((role) =>
		role?.startsWith('backoffice')
	)

	const reload = useReload()

	const notificationRecipients = useMemo(() => {
		const serviceProvidersToNotify = notify.filter(
			(provider) => !isNaN(Number(provider.value))
		)

		const list = recipientNotifications.reduce((acc, cur) => {
			const obj = {
				...cur,
				isDisabled:
					!isNaN(Number(cur.value)) &&
					serviceProvidersToNotify.length &&
					serviceProvidersToNotify.find(
						(elem) => elem.value === cur.value
					) === undefined,
			}
			acc.push(obj)

			return acc
		}, [])

		if (isAT && hasBackofficeRole) {
			list.unshift({
				value: 'trust_management',
				text: i18n.translate('Trust: Management'),
			})
		}

		return list
	}, [recipientNotifications, notify, isAT, hasBackofficeRole, i18n])

	const handleSubmit = (e) => {
		e.preventDefault()
		if (submitting) {
			return
		}
		const url = `${API_URL}/api/clinical_processes/${processId}/comments/reply`
		const formData = new FormData()

		const visibleToPublic = true
		let visibleToServiceProvider = false

		const {
			notifyInsuranceCompanyManagement,
			notifyInsuranceCompanyClinicalDirector,
			notifyTrustClinicalDirection,
			notifyTrustManagement,
			visibleToInsuranceCompany,
		} = notify.reduce(
			(result, selection) => {
				switch (selection.value) {
					case 'insurance_company_management':
						result.notifyInsuranceCompanyManagement = true
						break
					case 'insurance_company_clinical_director':
						result.notifyInsuranceCompanyClinicalDirector = true
						break
					case 'insurance_company_clinical_assistance':
						result.visibleToInsuranceCompany = true
						break
					case 'trust_clinical_direction':
						result.notifyTrustClinicalDirection = true
						break
					case 'trust_management':
						result.notifyTrustManagement = true
						break
					default:
						break
				}
				return result
			},
			{
				notifyInsuranceCompanyManagement: false,
				notifyInsuranceCompanyClinicalDirector: false,
				notifyTrustClinicalDirection: false,
				notifyTrustManagement: false,
				visibleToInsuranceCompany: false,
			}
		)

		const serviceProvidersToNotify = notify
			.filter((provider) => !isNaN(Number(provider.value)))
			.map((provider) => +provider.value)

		if (serviceProvidersToNotify.length > 0) {
			formData.append('notificationRecipients', serviceProvidersToNotify)
			visibleToServiceProvider = true
		}

		if (isDoctor) {
			formData.append('titleId', 1)
		} else {
			formData.append('titleId', parentType ?? 1)
		}

		formData.append('comment', comment)
		formData.append('parent', commentId)
		formData.append(
			'notifyInsuranceCompanyManagement',
			notifyInsuranceCompanyManagement
		)
		formData.append(
			'notifyInsuranceCompanyClinicalDirector',
			notifyInsuranceCompanyClinicalDirector
		)
		formData.append(
			'notifyTrustClinicalDirection',
			notifyTrustClinicalDirection
		)
		formData.append('notifyTrustManagement', notifyTrustManagement)
		formData.append('visibleToPublic', visibleToPublic)
		formData.append('visibleToServiceProvider', visibleToServiceProvider)
		formData.append('visibleToInsuranceCompany', visibleToInsuranceCompany)

		if (file) {
			formData.append('file', file, file?.name)
		}

		setSubmitting(true)

		fetch(url, fetchOptions({ method: 'POST', body: formData }))
			.then((r) => parseJSONContent(r))
			.then(() => {
				reload([
					`/clinical_processes/${processId}/comments/${commentId}/replies`,
					`/clinical_processes/${processId}/comments`,
				])

				pushNotification(
					undefined,
					'success',
					i18n`Comment inserted successfully!`
				)
			})
			.catch((err) => {
				if (err.status === '401' || err.status === 401) {
					expireSession()
				}

				setSubmitting(false)
			})
	}

	return (
		<div className={styles['comment-mini-form-root']}>
			<div className={styles['comment-mini-form']}>
				<div className={styles['comment-mini-form-horizontal-padded']}>
					{!isDoctor && (
						<div className={styles['comment-mini-form-recipient']}>
							<MultiSelect
								placeholder={i18n`For`}
								values={notify}
								options={notificationRecipients}
								onChange={(e) => {
									setNotify(e.target.value)
								}}
							/>
						</div>
					)}
					<TextInput
						placeholder={i18n`Write comment`}
						value={comment}
						multiline
						noResize
						rows='3'
						required
						onChange={(e) => {
							setComment(e.target.value)
						}}
						icon={() => null}
						className={styles['comment-mini-form-comment']}
					/>
				</div>
				<div className={styles['comment-mini-form-actions']}>
					<label className={styles['file-chooser']}>
						<HStack align='center' justify='space-between'>
							<Icon
								width='20px'
								height='20px'
								name='attachment'
								className={styles['attachments']}
							/>
							<VStack>
								<input
									type='file'
									onChange={(e) => {
										setFile(e.target.files[0])
									}}
								/>
								<small className={styles['filename']}>
									{file?.name}
								</small>
							</VStack>
						</HStack>
					</label>
					<div
						className={styles['comment-mini-form-actions-buttons']}
					>
						<button
							className={styles['footer-button']}
							onClick={closeMiniForm}
						>
							<small>{i18n`Cancel`}</small>
						</button>
						<button
							className={`${styles['footer-button']} ${
								submitting ? styles['--is-submitting'] : ''
							}`}
							onClick={handleSubmit}
							disabled={submitting}
						>
							<small
								className={
									styles[
										'comment-mini-form-actions-buttons-send'
									]
								}
							>
								{submitting ? i18n`Sending` : i18n`Send`}
							</small>
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default CommentThread
